'use client';

import React, { useMemo } from 'react';
import cx from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { Button } from './Button';
import { Logo } from './Logo';
import { PhoneIcon } from '@heroicons/react/20/solid';
import { Disclosure } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Search } from './Search/Search';

interface HeaderProps {
  planType?: string;
}

export const Header: React.FC<HeaderProps> = ({ planType }) => {
  const pathname = usePathname();

  const quoteUri = useMemo(
    () => `/quote${planType ? `?planType=${planType}` : ''}`,
    [planType],
  );

  const navItems = useMemo(
    () => [
      {
        label: 'Get Quote',
        uri: quoteUri,
        activePath: '/quote',
      },
      {
        label: 'Individual & Family',
        uri: `/individual-family`,
        activePath: '/individual-family',
      },
      {
        label: 'Medicare',
        uri: `/medicare`,
        activePath: `/medicare`,
      },
      {
        label: 'Dental',
        uri: `/dental`,
        activePath: `/dental`,
      },
      {
        label: 'Group',
        uri: `/group`,
        activePath: `/group`,
      },
      {
        label: 'Resources',
        uri: `/articles/all`,
        activePath: `/article`,
      },
      {
        label: 'Agents',
        uri: `/agents`,
        activePath: `/agents`,
      },
      {
        label: 'About',
        uri: `/about`,
        activePath: `/about`,
      },
    ],
    [quoteUri],
  );

  return (
    <header className="sticky z-20 top-0 min-h-[3.6rem] flex items-center bg-white shadow">
      <Disclosure as="nav" className="w-full">
        {({ open }) => (
          <>
            <div className="xl:container mx-auto flex justify-between px-4 lg:px-6">
              <div className="py-3 lg:py-6 mr-2 flex items-center">
                <Logo className="max-h-9 sm:max-h-[none]" />
              </div>
              <ul className="hidden lg:grid gap-3 min-[1048px]:gap-4 xl:gap-8 grid-flow-col text-center">
                {navItems.map(({ label, uri, activePath }) => (
                  <li key={label}>
                    <Link
                      href={uri}
                      className={cx(
                        'h-full flex items-center border-b-2 px-1 pt-1',
                        pathname?.startsWith(activePath)
                          ? 'border-blue-500  text-gray-900'
                          : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-700',
                      )}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
                <li className="h-full flex items-center">
                  <Search />
                </li>
              </ul>

              <div className="lg:hidden grid gap-3 sm:gap-5 grid-flow-col items-center">
                <Button
                  is="a"
                  variant="PRIMARY"
                  href="tel:1-800-797-0327"
                  className="whitespace-nowrap flex items-center"
                >
                  <PhoneIcon className="w-4 h-4 inline mr-2" />
                  Call
                </Button>
                <Search />
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
            <Disclosure.Panel className="lg:hidden container mx-auto space-y-1 pb-3">
              {navItems.map(({ label, uri, activePath }) => (
                <Disclosure.Button
                  as={Link}
                  href={uri}
                  key={uri}
                  className={cx(
                    'block border-l-4 py-2 pl-3 pr-4 text-base font-medium',
                    pathname?.startsWith(activePath)
                      ? 'border-blue-500 bg-blue-50  text-blue-700'
                      : 'border-transparent  text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 cursor-pointer',
                  )}
                >
                  {label}
                </Disclosure.Button>
              ))}
              <Button
                is={Link}
                variant="PRIMARY"
                href={quoteUri}
                className="mx-3"
              >
                Get Started
              </Button>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </header>
  );
};
